/**
 * Terminology Configuration Types
 *
 * Defines the structure for customizable terminology throughout
 * Cadence. Organizations can configure their own labels
 * and icons for key entity types.
 */

import { z } from 'zod'
import {
  ORG_UNIT_KINDS,
  type OrgUnitKindName,
  toOrgUnitKind,
} from '@/lib/org-units/kinds'
import { PRIORITY_KINDS, type PriorityKind } from '@/lib/priority-kinds'
import {
  PRIORITY_ROLE_TERMS,
  PRIORITY_TERM_HORIZONS,
  type PriorityHorizonTermKey,
  type PriorityRoleTerm,
  type PriorityTermOverrides,
  horizonTermKey,
} from '@/lib/priority-terms'

const TERMINOLOGY_COLOR_KEYS = [
  'blue',
  'purple',
  'emerald',
  'cyan',
  'indigo',
  'sky',
  'amber',
  'pink',
  'rose',
  'orange',
  'green',
  'red',
  'slate',
] as const

/** Keys recognized by the terminology color picker. Issue #5586. */
export type TerminologyColorKey = (typeof TERMINOLOGY_COLOR_KEYS)[number]

/**
 * Configuration for a single term (e.g., Theme, Big Rock)
 */
export const termConfigSchema = z.object({
  singular: z.string(),
  plural: z.string(),
  icon: z.string(),
  color: z.enum(TERMINOLOGY_COLOR_KEYS).optional(),
  isAcronym: z.boolean().optional(),
  shortDescription: z.string().optional(),
  longDescription: z.string().optional(),
  showOnWaysOfWorking: z.boolean().optional(),
  waysOfWorkingOrder: z.number().optional(),
})

export type TermConfig = z.infer<typeof termConfigSchema>

const partialTermConfigSchema = termConfigSchema.partial()

/**
 * The feature words (#15033: unchanged). Each names a feature, not a kind of
 * Priority. `issue` stays one: issues are blocker Beliefs (#15009), not a
 * Priority kind, and the word still drives the meetings IDS surfaces.
 */
export const FEATURE_TERMINOLOGY_KEYS = [
  'org',
  'handbook',
  'plan',
  'checkIn',
  'pulseCheck',
  'survey',
  'target',
  'issue',
  'meeting',
  'tag',
  'note',
] as const

export type FeatureTerminologyKey = (typeof FEATURE_TERMINOLOGY_KEYS)[number]

/**
 * The one horizon narrowing every org has a word for: a quarter outcome is
 * a "Rock" until the org says otherwise. Any other `kind·altitude` word is
 * the org's own, optional.
 */
export const ROCK_TERM_KEY =
  'outcome·quarter' as const satisfies PriorityHorizonTermKey

/**
 * Keys for terminology configuration: the feature words, a word per
 * Priority kind and per assignment role, and the quarter-outcome narrowing.
 */
export type TerminologyKey =
  | FeatureTerminologyKey
  | PriorityKind
  | PriorityRoleTerm
  | typeof ROCK_TERM_KEY

/**
 * Every key with a word in every org's config, in the order the settings
 * page lists them (#15033): the org, the priority kinds — a quarter outcome
 * beside `outcome` — the assignment roles, then the feature words.
 */
export const TERMINOLOGY_KEYS: readonly TerminologyKey[] = [
  'org',
  ...PRIORITY_KINDS.flatMap((kind): TerminologyKey[] =>
    kind === 'outcome' ? [kind, ROCK_TERM_KEY] : [kind],
  ),
  ...PRIORITY_ROLE_TERMS,
  ...FEATURE_TERMINOLOGY_KEYS.filter(key => key !== 'org'),
]

/**
 * Complete terminology configuration for all entity types.
 *
 * #15033 re-keyed the priority words: `rock`, `aor`, `measurable`,
 * `priority` and `task` became `outcome·quarter`, `responsibility`,
 * `measure`, `outcome` and `task`; `project` was retired. Optional narrowed
 * words (`outcome·year` → "Goal") ride beside the required keys; the
 * resolver in `@/lib/priority-terms` picks the most specific.
 */
export type TerminologyConfig = Record<TerminologyKey, TermConfig> &
  Partial<Record<PriorityHorizonTermKey, TermConfig>> &
  Partial<Record<OrgUnitTermKey, TermConfig>>

/** Joins the `unit` prefix and a unit kind: `unit·POD` (#16436). */
const ORG_UNIT_TERM_PREFIX = 'unit·'

/**
 * An org's word for one unit kind (#16436), stored beside its other words.
 * Optional: a kind the org never renamed has no entry and reads
 * {@link DEFAULT_ORG_UNIT_TERMS}.
 */
export type OrgUnitTermKey = `unit·${OrgUnitKindName}`

export function orgUnitTermKey(kind: OrgUnitKindName): OrgUnitTermKey {
  return `${ORG_UNIT_TERM_PREFIX}${kind}`
}

/** Every unit-kind key a stored config may carry, one per kind. */
export const ORG_UNIT_TERM_KEYS: readonly OrgUnitTermKey[] =
  ORG_UNIT_KINDS.map(orgUnitTermKey)

/** Every narrowed key, `kind·altitude`, kind by kind. */
const PRIORITY_HORIZON_TERM_KEYS: readonly PriorityHorizonTermKey[] =
  PRIORITY_KINDS.flatMap(kind =>
    PRIORITY_TERM_HORIZONS.map(horizon => horizonTermKey(kind, horizon)),
  )

/** Every key a stored config may carry: required and narrowed words. */
const STORED_TERMINOLOGY_KEYS: readonly string[] = [
  ...new Set<string>([
    ...TERMINOLOGY_KEYS,
    ...PRIORITY_HORIZON_TERM_KEYS,
    ...ORG_UNIT_TERM_KEYS,
  ]),
]

/**
 * Whether `key` is one a stored terminology row may carry — a required word,
 * a narrowed word or a unit-kind word. Anything else is dropped on write.
 */
export function isStoredTerminologyKey(key: string): boolean {
  return STORED_TERMINOLOGY_KEYS.includes(key)
}

export const terminologyConfigSchema = z.object(
  Object.fromEntries(
    TERMINOLOGY_KEYS.map(key => [key, termConfigSchema]),
  ) as Record<TerminologyKey, typeof termConfigSchema>,
)

/**
 * Stored per-org overrides. `z.object` strips unknown keys, so a row that
 * still holds a term this app no longer defines (a removed term, #15304)
 * loads with that key dropped rather than failing the whole parse. A legacy
 * noun key is re-keyed before the parse ({@link rekeyLegacyTerminology}).
 */
const partialTerminologyConfigSchema = z.object(
  Object.fromEntries(
    STORED_TERMINOLOGY_KEYS.map(key => [
      key,
      partialTermConfigSchema.optional(),
    ]),
  ),
)

export type PartialTerminologyConfig = Partial<
  Record<
    TerminologyKey | PriorityHorizonTermKey | OrgUnitTermKey,
    Partial<TermConfig>
  >
>

/** Where each legacy noun key's word lives now (#15033). */
export const LEGACY_TERMINOLOGY_KEY_MAP = Object.freeze({
  rock: ROCK_TERM_KEY,
  aor: 'responsibility',
  measurable: 'measure',
  priority: 'outcome',
  task: 'task',
} as const satisfies Record<string, TerminologyKey>)

type LegacyTerminologyKey = keyof typeof LEGACY_TERMINOLOGY_KEY_MAP

/**
 * Keys retired without a successor key. Project is no longer a term or a
 * concept (decided on #15033, 2026-10-08): a project is an `outcome`. The
 * key is never written back; its word becomes the outcome's when the org
 * did not customize `priority`, whose word the outcome takes first
 * ({@link rekeyLegacyTerminology}) — so a district seeded before the re-key
 * reads its "Initiative", as one seeded after it does.
 */
export const RETIRED_TERMINOLOGY_KEYS = ['project'] as const

/** What the retired `project` key defaulted to. */
const PROJECT_TERM_DEFAULT: Pick<TermConfig, 'singular' | 'plural' | 'icon'> = {
  singular: 'Project',
  plural: 'Projects',
  icon: 'clipboard-list',
}

/**
 * What each legacy key defaulted to. A stored legacy entry whose words and
 * icon are still these was saved, not chosen — `updateTerminology` writes
 * every key — so the new key's default applies instead.
 */
const LEGACY_TERM_DEFAULTS: Record<
  LegacyTerminologyKey,
  Pick<TermConfig, 'singular' | 'plural' | 'icon'>
> = {
  rock: { singular: 'Rock', plural: 'Rocks', icon: 'stone' },
  aor: { singular: 'AOR', plural: 'AORs', icon: 'landmark' },
  measurable: {
    singular: 'Measurable',
    plural: 'Measurables',
    icon: 'bar-chart-3',
  },
  priority: { singular: 'Priority', plural: 'Priorities', icon: 'target' },
  task: { singular: 'Task', plural: 'Tasks', icon: 'square-check' },
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Re-key a stored terminology object from the legacy noun keys (#15033) —
 * the TypeScript twin of the backfill in the `saved_views` migration,
 * applied on every read so a row the backfill has not reached, or one an
 * older release wrote during a deploy, reads the same.
 *
 *   - A legacy entry moves to its new key unless that key is already set,
 *     which wins: it was written by a release that knew the new keys.
 *   - Its words, and its icon, move only when the org chose them. Words still
 *     equal to the legacy default were saved, not chosen, so they are
 *     dropped and the new default applies. Everything else on the entry —
 *     color, descriptions, ways-of-working placement — moves as is.
 *   - `project`'s key is dropped on purpose ({@link RETIRED_TERMINOLOGY_KEYS}).
 *     Its entry moves to `outcome`, by the same rules, when nothing else
 *     set the outcome — no `outcome` key and no customized `priority`.
 *
 * Anything that is not a plain object is returned unchanged.
 */
export function rekeyLegacyTerminology(raw: unknown): unknown {
  if (!isRecord(raw)) return raw
  const out: Record<string, unknown> = { ...raw }
  for (const key of RETIRED_TERMINOLOGY_KEYS) delete out[key]
  for (const legacy of Object.keys(
    LEGACY_TERMINOLOGY_KEY_MAP,
  ) as LegacyTerminologyKey[]) {
    const next: string = LEGACY_TERMINOLOGY_KEY_MAP[legacy]
    const entry = raw[legacy]
    if (legacy !== next) delete out[legacy]
    if (!isRecord(entry) || (legacy !== next && raw[next] !== undefined)) {
      continue
    }
    const moved = chosenPart(entry, LEGACY_TERM_DEFAULTS[legacy])
    if (Object.keys(moved).length > 0) out[next] = moved
    else if (legacy === next) delete out[next]
  }
  // Project last: only when neither `outcome` nor `priority` set the outcome.
  const project = raw.project
  if (isRecord(project) && out.outcome === undefined) {
    const moved = chosenPart(project, PROJECT_TERM_DEFAULT)
    if (Object.keys(moved).length > 0) out.outcome = moved
  }
  return out
}

/**
 * A legacy entry without what it merely saved: words still equal to the
 * legacy default (singular and plural both), and the default icon.
 */
function chosenPart(
  entry: Record<string, unknown>,
  legacyDefault: Pick<TermConfig, 'singular' | 'plural' | 'icon'>,
): Record<string, unknown> {
  const moved: Record<string, unknown> = { ...entry }
  if (
    (moved.singular === undefined ||
      moved.singular === legacyDefault.singular) &&
    (moved.plural === undefined || moved.plural === legacyDefault.plural)
  ) {
    delete moved.singular
    delete moved.plural
    delete moved.isAcronym
  }
  if (moved.icon === legacyDefault.icon) delete moved.icon
  return moved
}

/**
 * Default terminology values.
 *
 * Feature words keep their long-standing defaults. A Priority kind and an
 * assignment role default to the system's own word (#15033, decided
 * 2026-10-08); a quarter outcome defaults to "Rock".
 */
export const DEFAULT_TERMINOLOGY: TerminologyConfig = {
  org: {
    singular: 'Org',
    plural: 'Orgs',
    icon: 'building-2',
  },
  anchor: { singular: 'Anchor', plural: 'Anchors', icon: 'compass' },
  value: { singular: 'Value', plural: 'Values', icon: 'gem' },
  principle: { singular: 'Principle', plural: 'Principles', icon: 'book-open' },
  responsibility: {
    singular: 'Responsibility',
    plural: 'Responsibilities',
    icon: 'landmark',
  },
  practice: { singular: 'Practice', plural: 'Practices', icon: 'list-checks' },
  measure: { singular: 'Measure', plural: 'Measures', icon: 'gauge' },
  outcome: { singular: 'Outcome', plural: 'Outcomes', icon: 'target' },
  [ROCK_TERM_KEY]: {
    singular: 'Rock',
    plural: 'Rocks',
    icon: 'stone', // Issue #1127 - Using stone icon from lucide-react 0.562+
  },
  task: {
    singular: 'Task',
    plural: 'Tasks',
    icon: 'square-check', // Issue #3491 - Task icon using SquareCheck from Lucide
  },
  captain: { singular: 'Captain', plural: 'Captains', icon: 'star' },
  contributor: {
    singular: 'Contributor',
    plural: 'Contributors',
    icon: 'users',
  },
  observer: { singular: 'Observer', plural: 'Observers', icon: 'user' },
  // #16117 — the page for the org and each part of it (`/handbook`), named
  // apart from `org` so renaming one never renames the other.
  handbook: {
    singular: 'Handbook',
    plural: 'Handbooks',
    icon: 'book-open',
  },
  plan: {
    singular: 'Plan',
    plural: 'Plans',
    icon: 'map', // Issue #2890 - Plan added to terminology system
  },
  checkIn: {
    singular: 'Check-in',
    plural: 'Check-ins',
    icon: 'clipboard-check', // Issue #2904 - Check-ins added to terminology system
  },
  pulseCheck: {
    singular: 'Pulse Check',
    plural: 'Pulse Checks',
    icon: 'activity', // Issue #2905 - Pulse Check added to terminology system
  },
  survey: {
    singular: 'Survey',
    plural: 'Surveys',
    icon: 'clipboard-list',
  },
  target: {
    singular: 'Target',
    plural: 'Targets',
    icon: 'target',
  },
  issue: {
    // #16995: what the room raises is something to talk through; one that
    // holds a priority up is a topic with a Blocks link. Was Issue / flag
    // (#7134, the EOS IDS list); the key stays `issue`.
    singular: 'Topic',
    plural: 'Topics',
    icon: 'message-square',
  },
  meeting: {
    singular: 'Meeting',
    plural: 'Meetings',
    icon: 'calendar-days', // Issue #7956 - Meeting added
  },
  tag: {
    singular: 'Tag',
    plural: 'Tags',
    icon: 'tag', // Issue #9847 - Arbitrary tags for work items
  },
  note: {
    singular: 'Note',
    plural: 'Notes',
    // Issue #14535 — `NAV_ENTRY_ICONS.notes` in `@/lib/ui-icons`, the icon `/notes`
    // already shows.
    icon: 'notebook-pen',
  },
}

/**
 * Default terminology per unit kind (`ORG_UNIT_KINDS`; #13412, #16417).
 *
 * `OrgUnit` is ONE table with TWO vocabularies. Nothing on the model is an
 * education noun — the standing schema test is "does this work for both a
 * school principal and a VP of Engineering?" — so what a reader SEES has to
 * come from here: a `LOCAL_ED_AGENCY` org labels `OrgUnit{SCHOOL}` "School",
 * a `COMPANY` labels `OrgUnit{DEPARTMENT}` "Department".
 *
 * Deliberately a SEPARATE map rather than fifteen more REQUIRED keys on
 * `TerminologyConfig`: every required key is rendered by the terminology
 * settings page and written into each org's config, and folding these in
 * would put a dozen rows in front of every admin for a choice most never
 * make. An org that does rename a kind (#16436 — "Pod" is "Squad" here) stores
 * an OPTIONAL `unit·<KIND>` entry in the same row ({@link orgUnitTermKey}),
 * layered over this default by `mergeTerminology`; pass the org's config to
 * {@link getOrgUnitTerm} / {@link getOrgUnitTermLabel} to read it.
 *
 * `OTHER` reads "Unit", the generic word, because that is what an unclassified
 * structural unit is in both vocabularies.
 */
export const DEFAULT_ORG_UNIT_TERMS: Record<OrgUnitKindName, TermConfig> = {
  SCHOOL: { singular: 'School', plural: 'Schools', icon: 'landmark' },
  CAMPUS: { singular: 'Campus', plural: 'Campuses', icon: 'building-2' },
  DIVISION: { singular: 'Division', plural: 'Divisions', icon: 'layers' },
  DEPARTMENT: {
    singular: 'Department',
    plural: 'Departments',
    icon: 'briefcase',
  },
  TEAM: { singular: 'Team', plural: 'Teams', icon: 'users-round' },
  REGION: { singular: 'Region', plural: 'Regions', icon: 'map' },
  OTHER: { singular: 'Unit', plural: 'Units', icon: 'component' },
  // The root `isSelf` unit (#16417, ADR-0138 § 2).
  ORGANIZATION: {
    singular: 'Organization',
    plural: 'Organizations',
    icon: 'building-2',
  },
  // The launch kinds of ADR-0138 decision 14.
  POD: { singular: 'Pod', plural: 'Pods', icon: 'users' },
  LEADERSHIP_TEAM: {
    singular: 'Leadership team',
    plural: 'Leadership teams',
    icon: 'compass',
  },
  WORKING_GROUP: {
    singular: 'Working group',
    plural: 'Working groups',
    icon: 'wrench',
  },
  COMMITTEE: { singular: 'Committee', plural: 'Committees', icon: 'handshake' },
  PLC: { singular: 'PLC', plural: 'PLCs', icon: 'book-open' },
}

/**
 * The term for a unit of `kind`: the org's word when `config` carries a
 * rename for it (#16436), else the default. Takes a `string` because
 * `OrgUnit.kind` is a String column (#16417): a kind this release does not
 * know reads as the generic `OTHER` term instead of throwing.
 */
export function getOrgUnitTerm(
  kind: string,
  config?: Pick<TerminologyConfig, OrgUnitTermKey> | null,
): TermConfig {
  const known = toOrgUnitKind(kind)
  return config?.[orgUnitTermKey(known)] ?? DEFAULT_ORG_UNIT_TERMS[known]
}

/**
 * The label for a unit of `kind` — the org's word when `config` is given and
 * renames it, the default otherwise.
 */
export function getOrgUnitTermLabel(
  kind: string,
  plural = false,
  config?: Pick<TerminologyConfig, OrgUnitTermKey> | null,
): string {
  const term = getOrgUnitTerm(kind, config)
  return plural ? term.plural : term.singular
}

/**
 * Get a term label from config
 */
export function getTermLabel(
  config: TerminologyConfig,
  key: TerminologyKey,
  plural = false,
): string {
  const term = config[key]
  return plural ? term.plural : term.singular
}

/**
 * Get a term icon name from config
 */
export function getTermIconName(
  config: TerminologyConfig,
  key: TerminologyKey,
): string {
  return config[key].icon
}

/**
 * Merge partial terminology config with defaults. Every required key takes
 * its default under what the org set; a narrowed word the org set
 * (`outcome·year`) rides along, filled from its kind's merged entry.
 */
export function mergeTerminology(
  partial: PartialTerminologyConfig | null | undefined,
): TerminologyConfig {
  if (!partial) return DEFAULT_TERMINOLOGY

  const merged = Object.fromEntries(
    TERMINOLOGY_KEYS.map(key => [
      key,
      { ...DEFAULT_TERMINOLOGY[key], ...partial[key] },
    ]),
  ) as TerminologyConfig
  for (const key of PRIORITY_HORIZON_TERM_KEYS) {
    const narrowed = partial[key]
    if (narrowed === undefined || key === ROCK_TERM_KEY) continue
    const kind = key.slice(0, key.indexOf('·')) as PriorityKind
    merged[key] = { ...merged[kind], ...narrowed }
  }
  // An org's word for a unit kind (#16436), over that kind's default.
  for (const kind of ORG_UNIT_KINDS) {
    const key = orgUnitTermKey(kind)
    const renamed = partial[key]
    if (renamed === undefined) continue
    merged[key] = { ...DEFAULT_ORG_UNIT_TERMS[kind], ...renamed }
  }
  return merged
}

/**
 * Load terminology from stored JSON. Null, missing, legacy-partial, and
 * malformed data all fail closed to a complete config for callers. A row
 * still keyed by the legacy nouns is re-keyed first (#15033).
 */
export function loadTerminologyConfig(raw: unknown): TerminologyConfig {
  if (raw == null) return DEFAULT_TERMINOLOGY

  const result = partialTerminologyConfigSchema.safeParse(
    rekeyLegacyTerminology(raw),
  )
  if (!result.success) return DEFAULT_TERMINOLOGY

  return mergeTerminology(result.data as PartialTerminologyConfig)
}

/**
 * The org's priority words, for `priorityTerms` (#15033): every kind, every
 * role and every narrowed word the config holds. This is how the re-keyed
 * terminology reaches the resolver — no parallel lookup.
 */
export function priorityTermOverridesFromTerminology(
  config: TerminologyConfig,
): PriorityTermOverrides {
  const overrides: PriorityTermOverrides = {}
  const keys = [
    ...PRIORITY_KINDS,
    ...PRIORITY_ROLE_TERMS,
    ...PRIORITY_HORIZON_TERM_KEYS,
  ]
  for (const key of keys) {
    const term = config[key]
    if (term === undefined) continue
    overrides[key] = {
      singular: term.singular,
      plural: term.plural,
      isAcronym: term.isAcronym,
    }
  }
  return overrides
}

/** A legacy noun key, or a retired one, still spoken by older data shapes. */
export type LegacyTermKey =
  | LegacyTerminologyKey
  | (typeof RETIRED_TERMINOLOGY_KEYS)[number]

/**
 * The current key for a key that may still be a legacy noun (#15033). Some
 * feeds and item types still speak `rock` / `aor` / `project`; this is where
 * they meet the re-keyed config. A project's word is the outcome's.
 */
export function toTerminologyKey(
  key: TerminologyKey | LegacyTermKey,
): TerminologyKey {
  if (key === 'project') return 'outcome'
  return Object.prototype.hasOwnProperty.call(LEGACY_TERMINOLOGY_KEY_MAP, key)
    ? LEGACY_TERMINOLOGY_KEY_MAP[key as LegacyTerminologyKey]
    : (key as TerminologyKey)
}

/**
 * The terminology key for a legacy item type (#15033): a rock is a quarter
 * outcome, a project an outcome, a group (AOR) a responsibility.
 */
export function termKeyForItemType(
  itemType: 'rock' | 'project' | 'group',
): TerminologyKey {
  if (itemType === 'group') return 'responsibility'
  if (itemType === 'project') return 'outcome'
  return ROCK_TERM_KEY
}

/**
 * Lowercase a term label unless it's an acronym.
 * Issue #5151 - Context-aware capitalization.
 */
export function getSentenceCaseLabel(
  label: string,
  isAcronym?: boolean,
): string {
  if (isAcronym) return label
  return label.toLowerCase()
}

/**
 * Get a term label from config in sentence case (lowercased unless acronym).
 * Issue #5151 - For use in sentences: "Select a rock..." / "Select an AOR..."
 */
export function getTermSentenceLabel(
  config: TerminologyConfig,
  key: TerminologyKey,
  plural = false,
): string {
  const term = config[key]
  const label = plural ? term.plural : term.singular
  return getSentenceCaseLabel(label, term.isAcronym)
}

/**
 * Available colors for terminology customization. Issue #5586.
 *
 * Each entry is a design-system color token (not a raw hex), so rebrands
 * stay coherent with the rest of the app and dark mode continues to work
 * through the semantic token layer. The `textClass` is the Tailwind
 * text-* class that consumers apply to Lucide icons when a term is
 * rendered with its configured color.
 *
 * Full class strings are required (no template literals) so Tailwind's
 * JIT compiler detects them — same convention as color-constants.ts.
 */
export const TERMINOLOGY_COLORS = [
  { key: 'blue', label: 'Blue', textClass: 'text-status-blue-text' },
  { key: 'purple', label: 'Purple', textClass: 'text-status-purple-text' },
  {
    key: 'emerald',
    label: 'Emerald',
    textClass: 'text-status-emerald-text-strong',
  },
  {
    key: 'cyan',
    label: 'Cyan',
    textClass: 'text-status-cyan-text-strong',
  },
  { key: 'indigo', label: 'Indigo', textClass: 'text-status-indigo-text' },
  { key: 'sky', label: 'Sky', textClass: 'text-status-sky-text' },
  {
    key: 'amber',
    label: 'Amber',
    textClass: 'text-status-amber-text-strong',
  },
  {
    key: 'pink',
    label: 'Pink',
    textClass: 'text-status-pink-text-strong',
  },
  { key: 'rose', label: 'Rose', textClass: 'text-status-rose-text' },
  {
    key: 'orange',
    label: 'Orange',
    textClass: 'text-status-orange-text-label',
  },
  { key: 'green', label: 'Green', textClass: 'text-status-green-text' },
  { key: 'red', label: 'Red', textClass: 'text-status-red-text' },
  { key: 'slate', label: 'Slate (default)', textClass: 'text-text-secondary' },
] as const

/**
 * Fast lookup from color key → text class. Issue #5586.
 *
 * Typed as `Record<TerminologyColorKey, string>` so indexing by a narrowed
 * `TerminologyColorKey` is total; the `unknown`-input path flows through
 * `isValidTermColor` instead.
 */
const TERMINOLOGY_COLOR_CLASS_MAP = Object.fromEntries(
  TERMINOLOGY_COLORS.map(c => [c.key, c.textClass]),
) as Record<TerminologyColorKey, string>

/**
 * Remembers keys that already warned once this process. Prevents log spam
 * when a stale JSON value flows through many renders. Issue #5586.
 */
const warnedUnknownColorKeys = new Set<string>()

/**
 * Resolve a terminology color key to its Tailwind text class.
 * Returns null when the key is undefined (no color configured) or
 * unrecognized (defensive — bad JSON from a prior rollback or manual
 * DB edit should not crash consumers). Issue #5586.
 *
 * When a non-empty string key isn't in the palette, emits a one-time
 * `console.warn` per key so stale/corrupt data surfaces in telemetry
 * without affecting render.
 */
export function getTermColorClass(
  key: TerminologyColorKey | undefined,
): string | null {
  if (!key) return null
  const resolved = TERMINOLOGY_COLOR_CLASS_MAP[key]
  if (!resolved) {
    if (typeof key === 'string' && !warnedUnknownColorKeys.has(key)) {
      warnedUnknownColorKeys.add(key)
      console.warn(
        `[terminology] Unknown color key "${key}" — falling back to default. ` +
          'This usually means a stale JSON value from a rollback or manual DB edit.',
      )
    }
    return null
  }
  return resolved
}

/**
 * Check if a color key is valid (exists in the palette). Issue #5586.
 * Used server-side to validate admin input before persisting to JSON.
 */
export function isValidTermColor(key: unknown): key is TerminologyColorKey {
  return typeof key === 'string' && key in TERMINOLOGY_COLOR_CLASS_MAP
}

/**
 * Test-only hook to reset the one-time warning memo. Issue #5586.
 * Production callers never invoke this — the `__` prefix signals intent.
 */
export function __resetTerminologyColorWarnings(): void {
  warnedUnknownColorKeys.clear()
}

/**
 * Per-term canonical default color. Issue #7134 review feedback.
 *
 * When a term has no `color` configured, consumers fall back to a
 * "hardcoded default" — historically that meant `text-text-secondary`
 * (gray), which made the admin terminology page look like every term
 * was uniformly slate. The actual app shows each entity in a distinct
 * color (blue rocks, purple AORs, emerald projects, etc.), so the
 * preview was lying about what users would see.
 *
 * This map mirrors `ENTITY_COLORS` in
 * `app/company-info/ways-of-working/ways-of-working-content.tsx` so
 * the admin preview and the public ways-of-working page agree on what
 * "default" looks like.
 */
export const DEFAULT_TERM_COLORS: Record<TerminologyKey, TerminologyColorKey> =
  {
    org: 'indigo',
    handbook: 'indigo',
    anchor: 'indigo',
    value: 'pink',
    principle: 'slate',
    responsibility: 'purple',
    practice: 'cyan',
    measure: 'amber',
    outcome: 'blue',
    [ROCK_TERM_KEY]: 'blue',
    captain: 'emerald',
    contributor: 'emerald',
    observer: 'slate',
    plan: 'indigo',
    checkIn: 'blue',
    pulseCheck: 'rose',
    survey: 'blue',
    task: 'sky',
    target: 'cyan',
    issue: 'orange',
    meeting: 'green',
    // Issue #9847 — tag color is org-configurable via Terminology (NOT per-tag).
    // Default slate: tags render as a neutral filled badge (like the other
    // identifier badges) so saturated color stays reserved for status.
    tag: 'slate',
    // Issue #14535 — the note taker's pink.
    note: 'pink',
  }

/**
 * Resolve the icon-tint class for a term, honoring the configured
 * color when set and falling back to the term's canonical default
 * color (NOT slate gray). Issue #7134 review feedback.
 *
 * Returns a non-null Tailwind class — every term has a default.
 */
export function getEffectiveTermColorClass(
  key: TerminologyKey,
  configured: TerminologyColorKey | undefined,
): string {
  const explicit = getTermColorClass(configured)
  if (explicit) return explicit
  // DEFAULT_TERM_COLORS values are TerminologyColorKey by construction,
  // so the inner lookup always resolves; the `??` is belt-and-braces
  // for a corrupt palette imported via stale JSON.
  return getTermColorClass(DEFAULT_TERM_COLORS[key]) ?? 'text-text-secondary'
}

/**
 * Substitute terminology tokens in a description string.
 *
 * Supports {{key}} and {{key:plural}} patterns, e.g.:
 *   "Each {{outcome·quarter}} sits in a {{responsibility}}" →
 *     "Each Pillar sits in a Department"
 *   "View all {{outcome·quarter:plural}}" → "View all Pillars"
 *
 * Tokens are case-preserved from the terminology config. A legacy noun token
 * (`{{rock}}`, `{{aor}}`, …) still resolves through its new key (#15033), so
 * a description written before the re-key keeps reading.
 */
export function substituteTerminology(
  text: string,
  config: TerminologyConfig,
): string {
  return text.replace(
    /\{\{([\w·]+)(?::plural)?\}\}/g,
    (match, key: string, _offset, _str) => {
      const isPlural = match.includes(':plural')
      const term = config[toTerminologyKey(key as TerminologyKey)]
      if (!term) return match
      return isPlural ? term.plural : term.singular
    },
  )
}
